import React, { useEffect, useMemo, useState } from "react";
import "./Header.css";
import { FaBars, FaTimes, FaPhoneAlt } from "react-icons/fa";
import { useNavigate, useLocation } from "react-router-dom";
import { useLocalContext } from "../../context/LocalContext";

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { webinfo = {} } = useLocalContext();

  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // close drawer on route change
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // lock body scroll when menu open
  useEffect(() => {
    document.body.style.overflow = menuOpen ? "hidden" : "";
    return () => (document.body.style.overflow = "");
  }, [menuOpen]);

  const navLinks = useMemo(
    () => [
      { label: "Home", path: "/" },
      { label: "Services", path: "/services" },
      { label: "Approach", path: "/strategies" },
      { label: "Contact", path: "/contact" },
    ],
    []
  );

  const brandName = (webinfo?.name || "Your Brand").trim();

  // ✅ support all your variants
  const phoneCall = (
    webinfo?.phonecall ||
    webinfo?.phoneCall ||
    webinfo?.phone_call ||
    ""
  ).trim();

  const phoneDisplay = (
    webinfo?.phone ||
    webinfo?.phoneDisplay ||
    (phoneCall ? phoneCall : "Call")
  ).trim();

  const isActive = (path) =>
    path === "/"
      ? location.pathname === "/"
      : location.pathname.startsWith(path);

  return (
    <>
      <header className={`hdx ${scrolled ? "isScrolled" : ""}`}>
        <div className="hdx__inner">
          {/* Brand */}
          <div className="hdx__brand" onClick={() => navigate("/")}>
            <span className="hdx__mark" aria-hidden="true" />
            <div className="hdx__brandText">
              <span className="hdx__name">{brandName}</span>
              <span className="hdx__tag">Web • Marketing • Automation</span>
            </div>
          </div>

          {/* Desktop nav */}
          <nav className="hdx__nav" aria-label="Primary navigation">
            {navLinks.map((l) => (
              <button
                key={l.path}
                className={`hdx__link ${isActive(l.path) ? "isActive" : ""}`}
                onClick={() => navigate(l.path)}
                type="button"
              >
                {l.label}
              </button>
            ))}
          </nav>

          {/* Actions */}
          <div className="hdx__actions">
            <button
              className="hdx__btn hdx__btnGhost"
              onClick={() => navigate("/contact")}
              type="button"
            >
              Get Quote
            </button>

            {/* ✅ phone now shows correctly */}
            <a
              className={`hdx__call ${phoneCall ? "" : "isDisabled"}`}
              href={phoneCall ? `tel:${phoneCall}` : undefined}
              onClick={(e) => {
                if (!phoneCall) e.preventDefault();
              }}
              aria-label="Call"
              title={phoneDisplay}
            >
              <span className="hdx__callIco">
                <FaPhoneAlt />
              </span>
              <span className="hdx__callText">
                <span className="t">Call</span>
                <span className="n">{phoneDisplay}</span>
              </span>
            </a>

            <button
              className="hdx__menuBtn"
              onClick={() => setMenuOpen(true)}
              type="button"
              aria-label="Open menu"
            >
              <FaBars />
            </button>
          </div>
        </div>
      </header>

      {/* Mobile overlay */}
      <div
        className={`hdx__overlay ${menuOpen ? "open" : ""}`}
        onClick={() => setMenuOpen(false)}
      />

      {/* Mobile drawer */}
      <aside className={`hdx__drawer ${menuOpen ? "open" : ""}`}>
        <div className="hdx__drawerTop">
          <div className="hdx__brand" onClick={() => navigate("/")}>
            <span className="hdx__mark" aria-hidden="true" />
            <div className="hdx__brandText">
              <span className="hdx__name">{brandName}</span>
              <span className="hdx__tag">Web • Marketing • Automation</span>
            </div>
          </div>

          <button
            className="hdx__close"
            onClick={() => setMenuOpen(false)}
            type="button"
            aria-label="Close menu"
          >
            <FaTimes />
          </button>
        </div>

        <div className="hdx__drawerLinks">
          {navLinks.map((l) => (
            <button
              key={l.path}
              className={`hdx__drawerLink ${isActive(l.path) ? "isActive" : ""}`}
              onClick={() => navigate(l.path)}
              type="button"
            >
              {l.label}
            </button>
          ))}
        </div>

        <div className="hdx__drawerCta">
          <button
            className="hdx__btn hdx__btnPrimary"
            onClick={() => navigate("/contact")}
            type="button"
          >
            Start a Project
          </button>

          <a
            className={`hdx__btn hdx__btnWide hdx__btnGhost ${phoneCall ? "" : "isDisabled"}`}
            href={phoneCall ? `tel:${phoneCall}` : undefined}
            onClick={(e) => {
              if (!phoneCall) e.preventDefault();
            }}
          >
            <FaPhoneAlt /> <span>{phoneDisplay}</span>
          </a>

          <p className="hdx__mini">
            Typical response time: <b>2–6 hours</b>
          </p>
        </div>
      </aside>
    </>
  );
};

export default Header;